import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DatePicker } from '@/components/ui/date-picker';
import { 
  FileText, FileSpreadsheet, FileCode, 
  Download, Calendar, Filter, Loader2
} from 'lucide-react';

// Define the available report formats
const formats = [
  { id: 'pdf', name: 'PDF', icon: <FileText className="h-5 w-5" /> },
  { id: 'xlsx', name: 'Excel', icon: <FileSpreadsheet className="h-5 w-5" /> },
  { id: 'html', name: 'HTML', icon: <FileCode className="h-5 w-5" /> },
  { id: 'csv', name: 'CSV', icon: <FileText className="h-5 w-5" /> }
];

// Define the available report types
const reportTypes = [
  { id: 'profit_loss', name: 'Profit & Loss Statement', description: 'Summary of revenues, costs, and expenses' },
  { id: 'balance_sheet', name: 'Balance Sheet', description: 'Snapshot of assets, liabilities, and equity' },
  { id: 'cash_flow', name: 'Cash Flow Statement', description: 'Cash inflows and outflows' },
  { id: 'financial_statement', name: 'Financial Statement', description: 'Detailed financial transactions' },
  { id: 'expense_report', name: 'Expense Report', description: 'Breakdown of business expenses' },
  { id: 'sales_report', name: 'Sales Report', description: 'Summary of sales and revenue' },
  { id: 'tax_report', name: 'Tax Report', description: 'Tax-related financial information' }
];

export default function JasperReportsPage() {
  const { toast } = useToast();
  const [location] = useLocation();
  const [selectedBusiness, setSelectedBusiness] = useState<{ id: number, name: string } | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Report parameters
  const [reportType, setReportType] = useState('profit_loss');
  const [format, setFormat] = useState('pdf');
  const [startDate, setStartDate] = useState<Date | undefined>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1) // First day of current month
  );
  const [endDate, setEndDate] = useState<Date | undefined>(new Date());
  const [asOfDate, setAsOfDate] = useState<Date | undefined>(new Date());
  
  // Advanced filtering options
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [minAmount, setMinAmount] = useState<string>('');
  const [maxAmount, setMaxAmount] = useState<string>('');
  const [customerVendorFilter, setCustomerVendorFilter] = useState<string>('');
  const [accountFilter, setAccountFilter] = useState<string>('all');
  const [includeSubaccounts, setIncludeSubaccounts] = useState(true);
  const [comparisonPeriod, setComparisonPeriod] = useState<string>('none');

  // Fetch businesses
  const { data: businesses = [] } = useQuery<Array<{ id: number, name: string }>>({ 
    queryKey: ['/api/businesses'] 
  });

  // Set the selected business when businesses are loaded
  useEffect(() => {
    if (businesses.length > 0 && !selectedBusiness) {
      setSelectedBusiness(businesses[0]);
    }
  }, [businesses, selectedBusiness]);

  // Find the selected report type
  const selectedReportType = reportTypes.find(rt => rt.id === reportType);

  const handleGenerateReport = async () => {
    if (!selectedBusiness) {
      toast({
        title: 'No business selected',
        description: 'Please select a business to generate reports',
        variant: 'destructive'
      });
      return;
    }

    setIsGenerating(true);

    try {
      // Format dates for API
      const formattedStartDate = startDate ? startDate.toISOString().split('T')[0] : undefined;
      const formattedEndDate = endDate ? endDate.toISOString().split('T')[0] : undefined;
      const formattedAsOfDate = asOfDate ? asOfDate.toISOString().split('T')[0] : undefined;

      // Create parameters based on report type
      const params: Record<string, any> = {};
      
      if (reportType === 'balance_sheet') {
        params.asOfDate = formattedAsOfDate;
      } else {
        params.startDate = formattedStartDate;
        params.endDate = formattedEndDate;
      }
      
      // Add advanced filtering parameters if enabled
      if (showAdvancedFilters) {
        if (categoryFilter !== 'all') {
          params.categoryFilter = categoryFilter;
        }
        
        if (minAmount) {
          params.minAmount = parseFloat(minAmount);
        }
        
        if (maxAmount) {
          params.maxAmount = parseFloat(maxAmount);
        }
        
        if (customerVendorFilter) {
          params.customerVendorFilter = customerVendorFilter;
        }
        
        if (accountFilter !== 'all') {
          params.accountFilter = accountFilter;
          params.includeSubaccounts = includeSubaccounts;
        }
        
        if (comparisonPeriod !== 'none') {
          params.comparisonPeriod = comparisonPeriod;
        }
      }

      // Generate the report directly with fetch for blob response
      const response = await fetch('/api/reports/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reportType,
          format,
          businessId: selectedBusiness.id,
          params
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = getFileName(reportType, format);
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Report generated',
        description: `${selectedReportType?.name} has been downloaded`
      });
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: 'Error generating report',
        description: 'An error occurred while generating the report',
        variant: 'destructive'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const getContentType = (format: string): string => {
    switch (format) {
      case 'pdf':
        return 'application/pdf';
      case 'xlsx':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'html':
        return 'text/html';
      case 'csv':
        return 'text/csv';
      default:
        return 'application/octet-stream';
    }
  };

  const getFileName = (reportType: string, format: string): string => {
    const reportName = selectedReportType?.name.replace(/\s+/g, '_').toLowerCase() || reportType;
    const dateStr = new Date().toISOString().split('T')[0];
    return `${reportName}_${dateStr}.${format}`;
  };

  return (
    <div className="container mx-auto p-4 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Reports</h1>
          <p className="text-muted-foreground">Generate detailed financial reports for your business</p>
        </div>
      </div>

      <Tabs defaultValue="generate" className="w-full">
        <TabsList>
          <TabsTrigger value="generate">Generate Reports</TabsTrigger>
          <TabsTrigger value="history">Report History</TabsTrigger>
        </TabsList>
        
        <TabsContent value="generate" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Report Generator</CardTitle>
              <CardDescription>
                Select the type of report you want to generate and the format
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="report-type">Report Type</Label>
                    <Select
                      value={reportType}
                      onValueChange={setReportType}
                    >
                      <SelectTrigger id="report-type">
                        <SelectValue placeholder="Select report type" />
                      </SelectTrigger>
                      <SelectContent>
                        {reportTypes.map(type => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedReportType && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {selectedReportType.description}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="format">Format</Label>
                    <div className="grid grid-cols-4 gap-2 mt-2">
                      {formats.map(fmt => (
                        <Button
                          key={fmt.id}
                          type="button"
                          variant={format === fmt.id ? "default" : "outline"}
                          className="flex flex-col items-center justify-center py-3 h-auto"
                          onClick={() => setFormat(fmt.id)}
                        >
                          {fmt.icon}
                          <span className="mt-2">{fmt.name}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {reportType === 'balance_sheet' ? (
                    <div>
                      <Label>As of Date</Label>
                      <div className="flex items-center space-x-2 mt-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <DatePicker
                          date={asOfDate}
                          onDateChange={setAsOfDate}
                          className="w-full"
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <Label>Start Date</Label>
                        <div className="flex items-center space-x-2 mt-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <DatePicker
                            date={startDate}
                            onDateChange={setStartDate}
                            className="w-full"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label>End Date</Label>
                        <div className="flex items-center space-x-2 mt-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <DatePicker
                            date={endDate}
                            onDateChange={setEndDate}
                            className="w-full"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div className="pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                      className="w-full flex items-center justify-center"
                    >
                      <Filter className="mr-2 h-4 w-4" />
                      {showAdvancedFilters ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
                    </Button>
                  </div>
                  
                  {showAdvancedFilters && (
                    <div className="space-y-4 border rounded-md p-4 mt-4">
                      <h3 className="font-medium text-md">Advanced Filters</h3>
                      
                      {/* Category Filter */}
                      <div>
                        <Label htmlFor="category-filter">Category</Label>
                        <Select
                          value={categoryFilter}
                          onValueChange={setCategoryFilter}
                        >
                          <SelectTrigger id="category-filter">
                            <SelectValue placeholder="Filter by category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Categories</SelectItem>
                            <SelectItem value="income">Income</SelectItem>
                            <SelectItem value="expense">Expense</SelectItem>
                            <SelectItem value="asset">Asset</SelectItem>
                            <SelectItem value="liability">Liability</SelectItem>
                            <SelectItem value="equity">Equity</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Amount Range */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="min-amount">Min Amount</Label>
                          <Input
                            id="min-amount"
                            type="number"
                            value={minAmount}
                            onChange={(e) => setMinAmount(e.target.value)}
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <Label htmlFor="max-amount">Max Amount</Label>
                          <Input
                            id="max-amount"
                            type="number"
                            value={maxAmount}
                            onChange={(e) => setMaxAmount(e.target.value)}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      
                      {/* Customer/Vendor Filter */}
                      <div>
                        <Label htmlFor="customer-vendor">Customer/Vendor</Label>
                        <Input
                          id="customer-vendor"
                          type="text"
                          value={customerVendorFilter}
                          onChange={(e) => setCustomerVendorFilter(e.target.value)}
                          placeholder="Filter by name"
                        />
                      </div>
                      
                      {/* Account Filter */}
                      <div>
                        <Label htmlFor="account-filter">Account</Label>
                        <Select
                          value={accountFilter}
                          onValueChange={setAccountFilter}
                        >
                          <SelectTrigger id="account-filter">
                            <SelectValue placeholder="Filter by account" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Accounts</SelectItem>
                            <SelectItem value="cash">Cash</SelectItem>
                            <SelectItem value="accounts_receivable">Accounts Receivable</SelectItem>
                            <SelectItem value="inventory">Inventory</SelectItem>
                            <SelectItem value="fixed_assets">Fixed Assets</SelectItem>
                            <SelectItem value="accounts_payable">Accounts Payable</SelectItem>
                            <SelectItem value="loans">Loans</SelectItem>
                            <SelectItem value="sales">Sales</SelectItem>
                            <SelectItem value="cost_of_goods">Cost of Goods</SelectItem>
                            <SelectItem value="operating_expenses">Operating Expenses</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {accountFilter !== 'all' && (
                          <div className="flex items-center space-x-2 mt-2">
                            <input
                              type="checkbox"
                              id="include-subaccounts"
                              checked={includeSubaccounts}
                              onChange={(e) => setIncludeSubaccounts(e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                            <Label htmlFor="include-subaccounts" className="text-sm font-normal">
                              Include subaccounts
                            </Label>
                          </div>
                        )}
                      </div>
                      
                      {/* Comparison Period */}
                      <div>
                        <Label htmlFor="comparison-period">Comparison Period</Label>
                        <Select
                          value={comparisonPeriod}
                          onValueChange={setComparisonPeriod}
                        >
                          <SelectTrigger id="comparison-period">
                            <SelectValue placeholder="Select comparison" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Comparison</SelectItem>
                            <SelectItem value="previous_period">Previous Period</SelectItem>
                            <SelectItem value="previous_year">Previous Year</SelectItem>
                            <SelectItem value="year_to_date">Year to Date</SelectItem>
                            <SelectItem value="quarter_to_date">Quarter to Date</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  <div className="pt-4">
                    <Button 
                      className="w-full" 
                      size="lg"
                      onClick={handleGenerateReport}
                      disabled={isGenerating || !selectedBusiness}
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Download className="mr-2 h-4 w-4" />
                          Generate Report
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Report History</CardTitle>
              <CardDescription>
                View and download previously generated reports
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center p-6 text-muted-foreground">
                <p>No reports have been saved yet. Generated reports will appear here.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}